import * as React from 'react';
import { useCallback } from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { useCurrentUser } from './hooks';
import { useRoutes, Routes } from '../navigation/routes';
import { PortalType } from '../navigation/portalType';
import { useAppState } from '../core/redux/hooks';
import { Spinner } from '../loading/Spinner';
import { COLORS } from '../globalStyle/colors';
import { css } from '@emotion/core';
import { PAGE_BG, PAGE_HEIGHT } from '../layout/pageStyle';
import { FLEX_COLUMN, FLEX_CENTERED } from '../layout/flexBox';


const styles = {
  spinner: css`
    width: 100px;
    height: 100px;
  `,
};


interface Props extends RouteProps {
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  path: string;
}


const loginUrlFor = (pathName: string, routes: Routes) => {
  if (pathName.toLowerCase().indexOf(PortalType.RATER_APPROVAL.toLowerCase()) > -1) {
    return routes.RaterApprovalLogin;
  }
  if (pathName.toLowerCase().indexOf(PortalType.REPORT_RECIPIENT.toLowerCase()) > -1) {
    return routes.ReportRecipientLogin;
  }
  return routes.Login;
};


/**
 * ProtectedRoute: a component that acts like a normal react-router Route, but checks if the user is logged in before
 * routing. If the user is not logged in, the user is redirected to the Login Page
 */
const ProtectedRoute: React.FC<Props> = props => {
  const isLoggedIn = useCurrentUser();
  const routes     = useRoutes();

  const sessionStorageLoaded = useAppState(state => state.sessionStorageLoaded);

  const { component, ...rest } = props;

  const renderFunc = useCallback(
    (routeProps: any) => isLoggedIn
      ? <props.component {...routeProps} />
      : <Redirect
        to={{
          pathname: loginUrlFor(routeProps.location.pathname, routes),
          state:    {
            from: routeProps.location,
            at:   new Date(),
          },
        }}
      />,
    // Eslint apparently doesn't see JSX tags as a usage...
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoggedIn, props.component],
  );

  return sessionStorageLoaded
    ? <Route {...rest} render={renderFunc} />
    : (
      <div css={[PAGE_BG, PAGE_HEIGHT, FLEX_COLUMN, FLEX_CENTERED]}>
        <Spinner fill={COLORS.GREEN} css={styles.spinner} />
      </div>
    );
};

export default ProtectedRoute;
